import { AssistantRuntimeProvider, useRemoteThreadListRuntime } from '@assistant-ui/react';
import { Suspense } from 'react';

import { Loading } from '#components/loading.js';
import { AttachmentStoreProvider } from '#screens/agent-workbench/attachment-store-provider.js';
import { WorkbenchRunStatusProvider } from '#screens/agent-workbench/workbench-run-status-provider.js';
import { ListReadyBarrier } from '#screens/agent-workbench/list-ready-barrier.js';
import { useWorkbenchRuntimeForActiveThread } from '#screens/agent-workbench/use-workbench-runtime-for-active-thread.js';

import type { RemoteThreadListAdapter } from '@assistant-ui/react';
import type { ReactNode } from 'react';
import type { AgentWorkbenchAnalyticsContext } from '#screens/agent-workbench/agent-workbench-analytics.js';

interface AgentWorkbenchRuntimeProviderProps {
	/** Remote thread-list adapter backing the conversation list (from `buildAgentWorkbenchThreadListAdapter`). */
	adapter: RemoteThreadListAdapter;
	/** Resolves once the adapter's initial `list()` call has settled. */
	listReady: Promise<void>;
	/** Workbench surface rendered inside the runtime (typically an `AgentWorkbenchPanel`). */
	children: ReactNode;
	/** Existing conversation selected when this runtime is first mounted. */
	initialConversationId?: string;
	/** Attach live to runs driven by other sessions or background workers. On by default. */
	watchRemoteRuns?: boolean;
	/** Analytics identity of this workbench surface. Omit to leave the surface uninstrumented. */
	analytics?: AgentWorkbenchAnalyticsContext;
}

/**
 * Binds an agent-workbench surface to a remote-thread-list runtime and provides it to
 * `children`. Every agent-workbench entry point (wizard step, results tab, package
 * family editor) wraps its panel in this so thread persistence and resumption
 * behave identically.
 *
 * @param props.adapter - Remote thread-list adapter backing the conversation list.
 * @param props.listReady - Gates rendering until the first `list()` settles.
 * @param props.children - Workbench surface to render inside the runtime.
 */
export function AgentWorkbenchRuntimeProvider({
	adapter,
	listReady,
	children,
	initialConversationId,
	watchRemoteRuns = true,
	analytics,
}: AgentWorkbenchRuntimeProviderProps) {
	/** Supplies the active thread runtime with this provider's per-surface settings. */
	function useActiveThreadRuntimeHook() {
		// eslint-disable-next-line react-compiler/react-compiler -- assistant-ui invokes this hook-shaped closure outside React's component tree.
		return useWorkbenchRuntimeForActiveThread({ watchRemoteRuns, analytics });
	}

	const runtime = useRemoteThreadListRuntime({
		adapter,
		initialThreadId: initialConversationId,
		runtimeHook: useActiveThreadRuntimeHook,
	});

	// The Suspense boundary must wrap the provider, not just the panel:
	// assistant-ui hosts the active thread's runtime hook as a *sibling* of
	// `children`, so the history-loading suspension on thread switch escapes any
	// boundary placed inside the provider and would otherwise unwind to the app
	// root and remount the whole page. `runtime` is created above the boundary,
	// so its state and the loaded thread list survive the fallback.
	// AttachmentStoreProvider sits outside Suspense for the same reason: the
	// hosted runtime hook reads the store, and the store instance must survive
	// fallback renders.
	return (
		<AttachmentStoreProvider>
			<WorkbenchRunStatusProvider>
				<Suspense fallback={<Loading />}>
					<AssistantRuntimeProvider runtime={runtime}>
						<ListReadyBarrier listReady={listReady}>{children}</ListReadyBarrier>
					</AssistantRuntimeProvider>
				</Suspense>
			</WorkbenchRunStatusProvider>
		</AttachmentStoreProvider>
	);
}
